import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import ModalDialogContext from "../../../../../Context/ModalDialog/ModalDialogContext";
import { NoAccessScreen, LoadingIndicator } from "../../../../../Component";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import { styles } from "./styles";
import {
  Box,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  withStyles,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { getAugmentationSources } from "../../../../../API/Entity";
import MultiSelect from "../../../../../Component/Forms/MultiSelect";
import {
  AttributesTable,
  AssociationsTable,
  DocumentsTable,
  AddressesTable,
} from "./Components";
import { recordTypes } from "../../../../../API/config";
import CustomButton from "../../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
};

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

const AugmentRecordDialog = (props) => {
  const {
    classes,
    isOpen,
    onClose,
    entity,
    listOptions,
    user,
    reloadEntity,
    hasRecordPermission,
    hasPermission,
    onAttributeChange,
    resetAttribute,
    onUnitChange,
    saveRecord,
  } = props;

  const { t } = useTranslation();

  const { showMessage, showErrorMessage, showConfirmation } =
    useContext(ModalDialogContext);

  const attrRef = useRef();

  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [availableSources, setAvailableSources] = useState();
  const [selectedSources, setSelectedSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [reloadParent, setReloadParent] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [reloadOnClose, setReloadOnClose] = useState(false);

  useEffect(() => {
    const evaluateAugmentPermission = async () => {
      const canAugmentData = await hasPermission(
        recordTypes.record.name,
        recordTypes.record.actions.augmentData.name,
      );
      setHasAccess(canAugmentData);
    };

    if (entity) {
      evaluateAugmentPermission();
    }
  }, [entity]);

  useEffect(() => {
    const loadAugmentationSources = async () => {
      setLoadingSources(true);
      try {
        const response = await getAugmentationSources(entity.id);
        const { data = [] } = response;
        setAvailableSources(data);
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoadingSources(false);
    };

    if (hasAccess) {
      loadAugmentationSources();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (availableSources && availableSources.length > 0) {
      handleSelectedSourcesChange(availableSources);
    }
  }, [availableSources]);

  useEffect(() => {
    if (reloadParent) {
      (async () => {
        await reloadEntity(true);
        setReloadParent(false);
      })();
    }
  }, [reloadParent]);

  const handleSelectedSourcesChange = (v) => {
    const srcs = [];
    for (let val of v) {
      const src = availableSources.find(({ value }) =>
        val.value ? val.value === value : val === value,
      );
      if (src) {
        srcs.push(src);
      }
    }
    setSelectedSources(srcs || []);
  };

  const renderNoSourceMsg = () => (
    <Paper className={classes.noticeShort}>
      <Typography component="p" variant="h6" className={classes.mutedText}>
        No source selected
      </Typography>
    </Paper>
  );

  const handleTabChange = (e, v) => {
    if (
      selectedSources.length > 0 &&
      tabIndex == 0 &&
      attrRef.current.hasUnsavedChanges()
    ) {
      e.preventDefault();
      showConfirmation({
        message: t("messages.record.augmentation.unsavedDataWarning"),
        onPressYes: async (event) => {
          attrRef.current.discardChanges();
          setTabIndex(v);
        },
      });
    } else {
      setTabIndex(v);
    }
  };

  const renderTabContent = (index) => {
    switch (index) {
      default: //NOSONAR
      case 0:
        return (
          <AttributesTable
            entity={entity}
            sources={selectedSources}
            availableSources={availableSources}
            listOptions={listOptions}
            hasRecordPermission={hasRecordPermission}
            onAttributeChange={onAttributeChange}
            resetAttribute={resetAttribute}
            onUnitChange={onUnitChange}
            saveRecord={saveRecord}
            reloadParent={setReloadParent}
            ref={attrRef}
          />
        );
      case 1:
        return (
          <AssociationsTable
            entity={entity}
            sources={selectedSources}
            availableSources={availableSources}
            user={user}
            reloadParent={setReloadOnClose}
            hasPermission={hasPermission}
            hasRecordPermission={hasRecordPermission}
          />
        );
      case 2:
        return (
          <DocumentsTable
            entity={entity}
            sources={selectedSources}
            availableSources={availableSources}
            reloadParent={setReloadOnClose}
            hasRecordPermission={hasRecordPermission}
          />
        );
      case 3:
        return (
          <AddressesTable
            entity={entity}
            sources={selectedSources}
            availableSources={availableSources}
            reloadParent={setReloadOnClose}
            hasRecordPermission={hasRecordPermission}
          />
        );
    }
  };

  const renderViewForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MultiSelect
          label={
            availableSources
              ? t("labels.record.augmentation.dialog.section.sources")
              : t("labels.common.loading")
          }
          options={availableSources || []}
          initialValues={selectedSources.map(({ value }) => value) || []}
          changeHandler={handleSelectedSourcesChange}
        />
      </Grid>
      {selectedSources.length > 0 ? (
        <Grid item container xs={12}>
          <Grid item xs={12}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
              className={classes.tabStyle}
            >
              <Tab
                label={t(
                  "labels.record.augmentation.dialog.section.attributes",
                )}
                {...a11yProps(0)}
              />
              <Tab
                label={t(
                  "labels.record.augmentation.dialog.section.associations",
                )}
                {...a11yProps(1)}
              />
              <Tab
                label={t("labels.record.augmentation.dialog.section.documents")}
                {...a11yProps(2)}
              />
              <Tab
                label={t("labels.record.augmentation.dialog.section.addresses")}
                {...a11yProps(3)}
              />
            </Tabs>
          </Grid>
          <Grid item xs={12}>
            <TabPanel value={tabIndex} index={0} className={classes.tabContent}>
              {selectedSources.length > 0
                ? renderTabContent(tabIndex)
                : renderNoSourceMsg()}
            </TabPanel>
            <TabPanel value={tabIndex} index={1} className={classes.tabContent}>
              {selectedSources.length > 0
                ? renderTabContent(tabIndex)
                : renderNoSourceMsg()}
            </TabPanel>
            <TabPanel value={tabIndex} index={2} className={classes.tabContent}>
              {selectedSources.length > 0
                ? renderTabContent(tabIndex)
                : renderNoSourceMsg()}
            </TabPanel>
            <TabPanel value={tabIndex} index={3} className={classes.tabContent}>
              {selectedSources.length > 0
                ? renderTabContent(tabIndex)
                : renderNoSourceMsg()}
            </TabPanel>
          </Grid>
        </Grid>
      ) : availableSources ? (
        <Grid item xs={12}>
          {renderNoSourceMsg()}
        </Grid>
      ) : (
        ""
      )}
    </Grid>
  );

  const handleClose = () => {
    if (
      selectedSources.length > 0 &&
      tabIndex == 0 &&
      attrRef.current.hasUnsavedChanges()
    ) {
      attrRef.current.discardChanges();
    }
    onClose(reloadOnClose);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      fullWidth={true}
      scroll="paper"
      maxWidth="lg"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {t("labels.record.augmentation.header")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ overflowX: "hidden" }}>
        {hasAccess ? (
          loading ? (
            renderLoadingIndicator()
          ) : (
            renderViewForm()
          )
        ) : (
          <NoAccessScreen />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton variant="secondary" isDisabled={loading} onClick={handleClose}>
          {t("labels.common.close")}
        </CustomButton>
        
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles, { withTheme: true })(AugmentRecordDialog);
